import React, { useState, useEffect } from 'react';
import {
    Box, Tab, Tabs, Typography, Button, Snackbar, Alert,
    Tooltip, Chip, Skeleton, Fade, useMediaQuery, useTheme, Badge
} from '@mui/material';
import Header from "../../components/header";
import SideNavbar from "../../components/sideNavbar";
import BookingPage from '../../view/booking/BookingPage';
import ScheduleSettings from '../../view/user/scheduler/ScheduleSettings';
import AllAppointmentsList from '../../view/appointment/AllAppointmentsList';
import UnifiedCalendarView from '../../view/appointment/UnifiedCalendarView';
import CalendarStatusBadge from '../../view/appointment/CalendarStatusBadge';
import ConnectedCalendarsPanel from '../../view/appointment/ConnectedCalendarsPanel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TuneIcon from '@mui/icons-material/Tune';
import WebIcon from '@mui/icons-material/Web';
import DevicesIcon from '@mui/icons-material/Devices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import axiosInstance from '../../services/axiosInstance';

// ─── Tab Panel ───────────────────────────────────────────────────────────────
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            <Fade in={value === index} timeout={350}>
                <Box sx={{ display: value === index ? 'block' : 'none' }}>
                    {children}
                </Box>
            </Fade>
        </div>
    );
}

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
function StatusSkeleton() {
    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={110} height={32} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={110} height={32} sx={{ borderRadius: 2 }} />
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const AppointmentBooking = () => {
    const [tabValue, setTabValue] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [statuses, setStatuses] = useState({ google: false, outlook: false, loading: true });
    const [copied, setCopied] = useState(false);
    const [totalCalendarAccounts, setTotalCalendarAccounts] = useState(0);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchStatuses();
    }, []);

    const fetchStatuses = async () => {
        const userId = localStorage.getItem('userId');
        if (!userId) return;

        try {
            const res = await axiosInstance.get('/calendar-accounts/status', { params: { userId } });
            if (res.data.success) {
                setStatuses({
                    google: res.data.google?.isConnected || false,
                    googleData: res.data.google || {},
                    outlook: res.data.outlook?.isConnected || false,
                    outlookData: res.data.outlook || {},
                    loading: false
                });
            } else {
                const [gRes, oRes] = await Promise.all([
                    axiosInstance.get('/calendar/status', { params: { userId } }),
                    axiosInstance.get('/outlook-calendar/status', { params: { userId } })
                ]);
                setStatuses({
                    google: gRes.data.isConnected,
                    outlook: oRes.data.isConnected,
                    loading: false
                });
            }
        } catch (error) {
            try {
                const userId2 = localStorage.getItem('userId');
                const [gRes, oRes] = await Promise.all([
                    axiosInstance.get('/calendar/status', { params: { userId: userId2 } }),
                    axiosInstance.get('/outlook-calendar/status', { params: { userId: userId2 } })
                ]);
                setStatuses({
                    google: gRes.data.isConnected,
                    outlook: oRes.data.isConnected,
                    loading: false
                });
            } catch (fallbackErr) {
                console.error("Error fetching statuses", fallbackErr);
                setStatuses(prev => ({ ...prev, loading: false }));
            }
        }
    };

    const handleConnectOutlook = async () => {
        try {
            const res = await axiosInstance.get('/outlook-calendar/auth-url');
            if (res.data.success) {
                window.location.href = res.data.url;
            }
        } catch (error) {
            console.error("Error getting outlook auth url", error);
            setSnackbar({ open: true, message: 'Failed to initiate Outlook connection', severity: 'error' });
        }
    };

    const handleConnectGoogle = async () => {
        try {
            const res = await axiosInstance.get('/calendar/auth-url');
            if (res.data.success) {
                window.location.href = res.data.url;
            }
        } catch (error) {
            console.error("Error getting google auth url", error);
            setSnackbar({ open: true, message: 'Failed to initiate Google connection', severity: 'error' });
        }
    };

    const handleShare = () => {
        const providerId = localStorage.getItem('userId');
        if (!providerId) {
            setSnackbar({ open: true, message: 'User ID not found!', severity: 'error' });
            return;
        }
        const url = `${window.location.host === 'localhost:3000' ? 'http://localhost:3000' : window.location.origin}/book?providerId=${providerId}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setSnackbar({ open: true, message: 'Booking link copied to clipboard!', severity: 'success' });
            setTimeout(() => setCopied(false), 2500);
        });
    };

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleCalendarSummaryChange = (summary) => {
        const total = (summary?.google?.count || 0) + (summary?.outlook?.count || 0);
        setTotalCalendarAccounts(total);
        // Update header status pills
        setStatuses(prev => ({
            ...prev,
            google: (summary?.google?.count || 0) > 0,
            googleData: summary?.google || {},
            outlook: (summary?.outlook?.count || 0) > 0,
            outlookData: summary?.outlook || {},
        }));
    };

    const connectedCount = [statuses.google, statuses.outlook].filter(Boolean).length;

    const tabs = [
        { label: 'Booking Page', icon: <WebIcon sx={{ fontSize: 18 }} /> },
        { label: 'Calendar View', icon: <CalendarMonthIcon sx={{ fontSize: 18 }} /> },
        { label: 'All Appointments', icon: <ListAltIcon sx={{ fontSize: 18 }} /> },
        { label: 'Schedule Settings', icon: <TuneIcon sx={{ fontSize: 18 }} /> },
        {
            label: 'Calendars',
            icon: (
                <Badge
                    badgeContent={totalCalendarAccounts || null}
                    color="primary"
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 15, minWidth: 15 } }}
                >
                    <DevicesIcon sx={{ fontSize: 18 }} />
                </Badge>
            )
        },
    ];

    return (
        <Box sx={{ bgcolor: '#f4f6fb', minHeight: '100vh' }}>
            <Box sx={{ ml: { xs: '0px', md: '280px' } }}>
                <SideNavbar />
                <Box sx={{ display: { xs: 'none', md: 'initial' } }}>
                    <Header title={'Appointment Booking System'} />
                </Box>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        px: { xs: 1.5, sm: 2.5, md: 3 },
                        pt: { xs: 2, md: 2.5 },
                        pb: 4,
                    }}
                >
                    {/* ── Sticky Navigation Tabs Card ────────────────────── */}
                    <Box
                        sx={{
                            bgcolor: '#fff',
                            borderRadius: 2.5,
                            mb: 2,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                            position: 'flex',
                            top: { xs: 56, md: 64 },
                            zIndex: 100,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Top Row: Tabs + Share Button */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: { xs: 1, md: 2 },
                                borderBottom: '1px solid #f0f0f0',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Tabs
                                value={tabValue}
                                onChange={handleChange}
                                aria-label="appointment navigation tabs"
                                variant={isMobile ? 'scrollable' : 'standard'}
                                scrollButtons={isMobile ? 'auto' : false}
                                TabIndicatorProps={{
                                    style: {
                                        height: 3,
                                        borderRadius: '3px 3px 0 0',
                                        background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                                    }
                                }}
                                sx={{
                                    minHeight: 56,
                                    flex: 1,
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: { xs: '0.80rem', md: '0.875rem' },
                                        color: '#78909c',
                                        minHeight: 56,
                                        px: { xs: 1.5, md: 2.5 },
                                        gap: 0.7,
                                        transition: 'color 0.2s, background 0.2s',
                                        borderRadius: '8px 8px 0 0',
                                        '&:hover': {
                                            color: '#1565c0',
                                            bgcolor: 'rgba(21,101,192,0.05)',
                                        },
                                        '&.Mui-selected': {
                                            color: '#1565c0',
                                            fontWeight: 700,
                                        },
                                    },
                                }}
                            >
                                {tabs.map((tab, idx) => (
                                    <Tab
                                        key={idx}
                                        id={`tab-${idx}`}
                                        label={tab.label}
                                        icon={tab.icon}
                                        iconPosition="start"
                                    />
                                ))}
                            </Tabs>

                            {/* Share Button */}
                            <Tooltip
                                title={copied ? 'Link copied!' : 'Copy booking link to share with clients'}
                                arrow
                                placement="bottom"
                            >
                                <Button
                                    variant="contained"
                                    size={isMobile ? 'small' : 'medium'}
                                    startIcon={copied ? <DoneIcon /> : <ContentCopyIcon />}
                                    onClick={handleShare}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        fontSize: '0.80rem',
                                        bgcolor: copied ? '#4caf50' : '#1565c0',
                                        color: '#fff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        whiteSpace: 'nowrap',
                                        px: 2,
                                        '&:hover': {
                                            bgcolor: copied ? '#43a047' : '#0d47a1',
                                        },
                                    }}
                                >
                                    {copied ? 'Copied!' : 'Share Booking Link'}
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* ── Tab Panels ──────────────────────────────────────── */}
                    <Box
                        sx={{
                            bgcolor: '#fff',
                            borderRadius: 2.5,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                            overflow: 'hidden',
                        }}
                    >
                        <TabPanel value={tabValue} index={0}>
                            <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                                <BookingPage showDashboard={true} />
                            </Box>
                        </TabPanel>
                        <TabPanel value={tabValue} index={1}>
                            <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                                <UnifiedCalendarView activeTab={tabValue} />
                            </Box>
                        </TabPanel>
                        <TabPanel value={tabValue} index={2}>
                            <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                                <AllAppointmentsList />
                            </Box>
                        </TabPanel>
                        <TabPanel value={tabValue} index={3}>
                            <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                                <ScheduleSettings />
                            </Box>
                        </TabPanel>
                        <TabPanel value={tabValue} index={4}>
                            <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                                <ConnectedCalendarsPanel onStatusChange={handleCalendarSummaryChange} />
                            </Box>
                        </TabPanel>
                    </Box>
                </Box>
            </Box>

            {/* ── Snackbar ────────────────────────────────────────────────── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        fontWeight: 500,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AppointmentBooking;
